import { useCallback, useMemo } from 'react';

import { PostFilter, Sentiment, type PostFilterMap } from '../../../../entities/post/models';
import { useFilteredPostsNavigation } from '../../filtered-posts/hook/use-filtered-posts-navigation';
import { useFilteredProfilesNavigation } from '../../filtered-profiles/hook/use-filtered-profiles-navigation';
import { getBrandProductColumns } from '../configs/brand-product.table.columns';
import { showDrillDownContextMenu } from '../overlay/show-drill-down-context-menu';
import type { BrandWithThemes, DrillDownContext, ProductWithThemes, VolumeType } from '../types';

import { PageNavigation } from '@/shared/components/page-navigation/page-navigation';
import { StaticTable } from '@/shared/components/static-table/static-table';
import { useStaticTable } from '@/shared/components/static-table/use-static-table';

interface Props {
  data: BrandWithThemes[] | ProductWithThemes[];
  type: 'brands' | 'products';
  volumeType: VolumeType;
  onDrillDown: (context: DrillDownContext) => void;
  isLoading: boolean;
}

const PAGE_SIZE = 10;

export function BrandProductTable({ data, type, volumeType, onDrillDown, isLoading }: Props) {
  const navigateToFilteredPosts = useFilteredPostsNavigation();
  const navigateToFilteredProfiles = useFilteredProfilesNavigation();

  const handleRowClick = async (
    item: BrandWithThemes | ProductWithThemes,
    event: React.MouseEvent,
  ) => {
    // Skip if clicking on theme buttons
    if ((event.target as HTMLElement).closest('[data-theme-button]')) {
      return;
    }

    const action = await showDrillDownContextMenu({
      position: { x: event.clientX, y: event.clientY },
    });

    const filter: PostFilterMap = {};
    if (type === 'brands') {
      filter[PostFilter.Brand] = item.name;
    } else {
      const product = item as ProductWithThemes;
      filter[PostFilter.Product] = product.name;
      if (product.subname) {
        filter[PostFilter.Brand] = product.subname;
      }
    }

    switch (action) {
      case 'view-posts':
        await navigateToFilteredPosts({
          filter,
          dimension: type === 'brands' ? PostFilter.Brand : PostFilter.Product,
        });
        break;
      case 'view-profiles':
        await navigateToFilteredProfiles({
          filter,
          dimension: type === 'brands' ? PostFilter.Brand : PostFilter.Product,
        });
        break;
      case 'close':
        break;
    }
  };

  const handleThemeClick = useCallback(
    (item: BrandWithThemes | ProductWithThemes, sentiment: Sentiment) => {
      onDrillDown({
        type: type === 'brands' ? 'brand' : 'product',
        name: item.name,
        sentiment,
      });
    },
    [onDrillDown, type],
  );

  // Get columns configuration
  const columns = useMemo(() => {
    return getBrandProductColumns(type, volumeType, handleThemeClick);
  }, [type, volumeType, handleThemeClick]);

  // Setup table with pagination
  const { table, gridTemplateColumns } = useStaticTable({
    data: data || [],
    columns,
    pageSize: PAGE_SIZE,
    showIndexColumn: false, // We provide our own index column in the column configs
  });

  return (
    <>
      <div className="px-6 pb-6">
        <StaticTable
          isLoading={isLoading}
          pageSize={PAGE_SIZE}
          table={table}
          gridTemplateColumns={gridTemplateColumns}
          onRowClick={handleRowClick}
        />
      </div>
      {!isLoading && <PageNavigation table={table} className="border-base-border border-t" />}
    </>
  );
}
